import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import axios from "axios";
import { RefreshCw, AlertTriangle, School, Users, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { getBackendUrl } from "@/lib/backend";
import { useScope } from "@/context/ScopeContext";
import { toast } from "sonner";
import "../pages/SqafDashboard.css";

const API = `${getBackendUrl()}/api`;
const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");
const pct = (v) => (typeof v === "number" ? `${v.toFixed(1)}%` : "—");

const TAB_COPY = {
  executive: "District composite score, headline KPIs and programme posture.",
  blocks: "Block-level comparison and spread across the selected scope.",
  domains: "Domain and indicator breakdown driving the composite score.",
  insights: "Policy-ready observations based on current operational data.",
};

const ProgramDashboard = () => {
  const { programId, config } = useOutletContext();
  const { scope } = useScope();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "executive";
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!programId) return;
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const params = {};
      if (scope.districtCode) params.district_code = scope.districtCode;
      if (scope.blockCode) params.block_code = scope.blockCode;
      if (scope.udiseCode) params.udise_code = scope.udiseCode;
      if (scope.districtName) params.district_name = scope.districtName;
      if (scope.blockName) params.block_name = scope.blockName;
      if (!scope.districtName && !scope.districtCode) params.district_name = "PUNE";

      const res = await axios.get(`${API}/program-dashboards/${programId}/overview`, { params });
      setData(res.data || null);
    } catch (error) {
      console.error(`Failed to load ${programId} dashboard`, error);
      toast.error(`Failed to load ${config?.title || "program"} data`);
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [
    programId,
    config?.title,
    scope.version,
    scope.districtCode,
    scope.blockCode,
    scope.udiseCode,
    scope.districtName,
    scope.blockName,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const blockChart = useMemo(() => {
    const rows = (data?.blocks || [])
      .filter((b) => b.block_name)
      .map((b) => ({
        name: b.block_name,
        score: Number(b.score ?? b.total_enrolment ?? 0),
      }));
    return rows.slice(0, 12);
  }, [data?.blocks]);

  const domainRows = data?.domains || [];
  const insights = (data?.insights || []).filter(Boolean);
  const summary = data?.summary || {};

  const kpiCards = useMemo(() => {
    const cards = [
      { label: "Composite Score", value: pct(data?.composite_score), icon: BarChart3 },
      { label: "Schools", value: fmt(summary.total_schools), icon: School },
      { label: "Students", value: fmt(summary.total_students), icon: Users },
    ];
    if (summary.primary_share_pct != null) {
      cards.push({ label: "Primary Share", value: pct(summary.primary_share_pct), icon: BarChart3 });
    }
    if (summary.early_age_pct != null) {
      cards.push({ label: "Early-Age (4–6)", value: pct(summary.early_age_pct), icon: BarChart3 });
    }
    if (summary.largest_stage) {
      cards.push({ label: "Largest Stage", value: summary.largest_stage, icon: BarChart3 });
    }
    if (summary.completion_rate != null) {
      cards.push({ label: "Data Completion", value: pct(summary.completion_rate), icon: BarChart3 });
    }
    if (summary.aadhaar_coverage != null) {
      cards.push({ label: "Aadhaar Coverage", value: pct(summary.aadhaar_coverage), icon: BarChart3 });
    }
    if (summary.ctet_pct != null) {
      cards.push({ label: "CTET Qualified", value: pct(summary.ctet_pct), icon: BarChart3 });
    }
    return cards.slice(0, 4);
  }, [data?.composite_score, summary]);

  const renderExecutive = () => (
    <>
      <div className="sq-kpi-grid">
        {kpiCards.map((card) => (
          <div key={card.label} className="sq-kpi-card">
            <div className="sq-kpi-icon" aria-hidden="true">
              <card.icon strokeWidth={2} />
            </div>
            <div>
              <span>{card.label}</span>
              <strong>{loading ? "…" : card.value}</strong>
            </div>
          </div>
        ))}
      </div>

      <div className="sq-panel-grid">
        <section className="sq-panel">
          <h3>Domain Snapshot</h3>
          <div className="sq-domain-list">
            {domainRows.length ? (
              domainRows.map((d) => (
                <div key={d.id || d.label} className="sq-domain-row">
                  <div>
                    <strong>{d.label}</strong>
                    <span>{d.source}</span>
                  </div>
                  <b>{pct(d.value)}</b>
                </div>
              ))
            ) : (
              <p className="sq-empty-copy">No domain indicators available for this scope.</p>
            )}
          </div>
        </section>

        <section className="sq-panel">
          <h3>Block Comparison</h3>
          {blockChart.length ? (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={blockChart} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-28} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                    {blockChart.map((_, i) => (
                      <Cell key={i} fill={config?.accent || "#2563eb"} fillOpacity={0.85} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="sq-empty-copy">No block-level data for this scope.</p>
          )}
        </section>
      </div>
    </>
  );

  const renderBlocks = () => (
    <section className="sq-panel sq-panel-full">
      <h3>Block Performance</h3>
      {blockChart.length ? (
        <div style={{ width: "100%", height: 360 }}>
          <ResponsiveContainer>
            <BarChart data={blockChart} margin={{ top: 8, right: 8, left: 0, bottom: 56 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={72} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="score" fill={config?.accent || "#2563eb"} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="sq-empty-copy">No block performance data available.</p>
      )}
    </section>
  );

  const renderDomains = () => (
    <section className="sq-panel sq-panel-full">
      <h3>{activeTab === "domains" && programId === "ger" ? "Stage Breakdown" : "Domain Indicators"}</h3>
      <div className="sq-domain-grid">
        {domainRows.map((d) => (
          <div key={d.id || d.label} className="sq-domain-card">
            <span className="sq-domain-label">{d.label}</span>
            <strong className="sq-domain-value">{pct(d.value)}</strong>
            {d.enrolment != null ? <small>{fmt(d.enrolment)} students</small> : null}
            <small>{d.source}</small>
          </div>
        ))}
      </div>
      {(data?.stage_distribution || []).length ? (
        <div className="sq-stage-table-wrap">
          <table className="sq-simple-table">
            <thead>
              <tr>
                <th>Stage</th>
                <th>Enrolment</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {data.stage_distribution.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  <td>{fmt(row.enrolment)}</td>
                  <td>{pct(row.share_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );

  const renderInsights = () => (
    <section className="sq-panel sq-panel-full">
      <h3>Policy Insights</h3>
      {insights.length ? (
        <ul className="sq-insight-list">
          {insights.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="sq-empty-copy">Insights will appear once programme data is available.</p>
      )}
    </section>
  );

  const tabContent = {
    executive: renderExecutive,
    blocks: renderBlocks,
    domains: renderDomains,
    insights: renderInsights,
  }[activeTab] || renderExecutive;

  return (
    <div className="sq-page">
      <div className="sq-page-head">
        <div>
          <p className="sq-page-eyebrow">{config?.eyebrow}</p>
          <h1>{config?.title}</h1>
          <p className="sq-page-sub">{TAB_COPY[activeTab] || config?.description}</p>
        </div>
        <button type="button" className="sq-refresh-btn" onClick={() => fetchData(true)} disabled={refreshing}>
          <RefreshCw className={refreshing ? "spin" : ""} strokeWidth={2} />
          Refresh
        </button>
      </div>

      {data?.data_notice ? (
        <div className="sq-notice-banner">
          <AlertTriangle strokeWidth={2} />
          <span>{data.data_notice}</span>
        </div>
      ) : null}

      {loading ? (
        <div className="sq-loading-panel">Loading {config?.title}…</div>
      ) : !data?.has_data ? (
        <div className="sq-empty-panel">
          <p>No programme data available for the current scope.</p>
          <span>Import the relevant dataset or adjust district/block filters.</span>
        </div>
      ) : (
        tabContent()
      )}
    </div>
  );
};

export default ProgramDashboard;
